import EcosystemPlatformSelect from '@/components/Ecosystems/EcosystemPlatformSelect'
import EcosystemSelect from '@/components/Ecosystems/EcosystemSelect'
import EcosystemModulesToggle from '@/components/Ecosystems/EcosystemModulesToggle'
import EcosystemWebhookSettings from '@/components/Ecosystems/EcosystemWebhookSettings'
import { formatEcosystemName } from '@/common/ecosystemName'

// vue-gettext's interpolation breaks on null, and an empty hostname is common
const shown = value => (value === null || value === undefined || value === '' ? '—' : value)

const ecosystemsModel = vm => ({
  title: vm.$gettext('Ecosystems'),
  endpoint: 'ecosystems',
  // "+ NEW" leads to the bespoke creation page (/new/ecosystems), declared before the generic /new/:type route
  allowNew: true,
  allowDelete: false,
  canAccess: componentVm => componentVm.isMoova && componentVm.isSuperAdmin,
  beforeEditFilter: (payload, { original }) => {
    const { name, ...rest } = payload
    // The backend answers 409 to any `name` in the PATCH, even the ecosystem's own, so an unchanged name is not sent
    const result = name === original.name ? rest : payload
    // An emptied input means no hostname, as in the ecosystems that never had one
    if (result.hostname === '') {
      result.hostname = null
    }
    // Renaming changes the client users type to log in; the hostname is how each frontend finds its tenant
    const risky = [
      result.name !== undefined &&
        vm.$gettextInterpolate(vm.$gettext('Name: %{from} → %{to}'), { from: shown(original.name), to: shown(result.name) }),
      result.hostname !== undefined &&
        result.hostname !== original.hostname &&
        vm.$gettextInterpolate(vm.$gettext('Hostname: %{from} → %{to}'), { from: shown(original.hostname), to: shown(result.hostname) })
    ].filter(Boolean)
    const warning = vm.$gettext("These changes can lock the ecosystem's users out of login or of their frontends:")
    if (risky.length && !confirm(warning + '\n\n' + risky.join('\n'))) {
      return false
    }
    return result
  },
  fields: [
    {
      key: 'id',
      sortable: true
    },
    {
      key: 'name',
      label: vm.$gettext('Name'),
      sortable: true,
      editable: true,
      required: true,
      // Legacy names may keep uppercase letters; the formatter lower-cases whatever is typed, as the backend does
      pattern: '[A-Za-z0-9_\\-]+',
      inputFormatter: formatEcosystemName,
      description: vm.$gettext(
        'Only lowercase letters, numbers, hyphens (-) and underscores (_): spaces become hyphens and accents are removed as you type. It is the client users type to log in, so renaming it changes how all of them log in.'
      )
    },
    {
      key: 'hostname',
      label: vm.$gettext('Hostname'),
      sortable: true,
      editable: true,
      pattern: '[A-Za-z0-9\\-]+(\\.[A-Za-z0-9\\-]+)+',
      description: vm.$gettext('Each frontend finds its ecosystem by this domain: a wrong value leaves it unrecognized.')
    },
    {
      key: 'description',
      label: vm.$gettext('Description'),
      sortable: false,
      editable: true,
      type: 'textarea'
    },
    {
      key: 'default_ecosystem_id',
      label: vm.$gettext('Default ecosystem'),
      sortable: true,
      editable: true,
      required: true,
      description: vm.$gettext('Ecosystem whose models and settings this one reads; usually itself'),
      componentEdit: () => ({
        is: EcosystemSelect,
        props: { required: true }
      })
    },
    {
      key: 'outsourcing_platform_id',
      label: vm.$gettext('3P platform'),
      sortable: false,
      editable: true,
      description: vm.$gettext('3P platform that outsources to this ecosystem'),
      componentEdit: (value, key, item) => ({
        is: EcosystemPlatformSelect,
        props: { ecosystemId: item.id }
      })
    },
    // JSON columns: the backend replaces each object as a whole on PATCH, it doesn't merge keys
    {
      key: 'front_config',
      label: vm.$gettext('Front Config'),
      editable: true,
      type: 'JSON'
    },
    {
      key: 'branding',
      label: vm.$gettext('Branding'),
      editable: true,
      type: 'JSON'
    },
    {
      key: 'webhook_settings',
      label: vm.$gettext('Webhook settings'),
      editable: true,
      componentEdit: () => ({
        is: EcosystemWebhookSettings
      })
    },
    {
      key: 'shares_from',
      label: vm.$gettext('Shares from'),
      editable: true,
      description: vm.$gettext('Ecosystems whose shared models this one can read'),
      componentEdit: (value, key, item) => ({
        is: EcosystemSelect,
        props: { multiple: true, excludeId: item.id }
      })
    },
    {
      key: 'meta',
      label: vm.$gettext('Meta'),
      editable: true,
      type: 'JSON'
    },
    // Not editable on purpose: each switch saves itself with PUT /ecosystems/{id}/modules/{module},
    // so `modules` never travels in the form's PATCH
    {
      key: 'modules',
      label: vm.$gettext('Paid modules'),
      description: vm.$gettext('Products this ecosystem pays for. Turning one off cuts its users out of it.'),
      componentEdit: (value, key, item) => ({
        is: EcosystemModulesToggle,
        props: { ecosystemId: item.id }
      })
    }
  ],
  filters: ['id', 'name', 'hostname'],
  exclude: ['front_config', 'branding', 'webhook_settings', 'shares_from', 'meta', 'outsourcing_platform_id', 'modules']
})

export default ecosystemsModel
